import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { firstValueFrom, Subscription } from 'rxjs';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { CompanyCityModel } from 'src/app/models/management/company-city.model';
import { CityService } from 'src/app/services/management/city.service';
import { CompanyCityService } from 'src/app/services/management/company-city.service';
import { CountryService } from 'src/app/services/management/country.service';
import { DepartmentService } from 'src/app/services/management/department.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-register-company-city',
  templateUrl: './register-company-city.component.html',
  styleUrls: ['./register-company-city.component.scss'],
  standalone: false
})
export class RegisterCompanyCityComponent implements OnInit {

  formCompanyCity: FormGroup = new FormGroup({});
  companyCityModel: CompanyCityModel = new CompanyCityModel();
  companyCityId: number = 0;
  countryId: number = 0;
  departmentId: number = 0;
  subscription: Subscription;

  nameWindow: string = 'Códigos Ciudades';
  permissionUpdatable: boolean = false;
  projectName = ProjectName.projectName;
  disabledForm: boolean = false;
  url: string = 'company-city';
  isOpened: string | undefined = undefined;
  listDepartments: any[] = [];
  listCountries: any[] = [];
  listCities: any[] = [];

  constructor(private companyCityService: CompanyCityService,
    public config: DynamicDialogConfig,
    private countryService: CountryService,
    private cityService: CityService,
    private cdr: ChangeDetectorRef,
    private departmentService: DepartmentService,
    private formBuilder: FormBuilder,
    private toastHandlerService: ToastHandlerService) {
    this.validateForm(new CompanyCityModel());
  }

  async ngOnInit() {
    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }
    this.subscription = input$?.subscribe((inputs: any) => {
      this.companyCityModel = inputs.companyCityModel;
      this.permissionUpdatable = inputs.permissionUpdatable;
    });
    await this.initializeFreightData();
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  async changeCity(cityId: number | null) {
    if (cityId) {
      await this.getCompanyCityByCityId(cityId);
    }
  }

  async createCompanyCity(modelCompanyCity: CompanyCityModel) {
    try {
      const response = await firstValueFrom(this.companyCityService.createCompanyCity(modelCompanyCity));
      this.companyCityId = response.companyCityId;
      this.companyCityModel = response;
      this.toastHandlerService.showToast('success', `El código de la ciudad fue creado exitosamente.`);
      this.onReloadEmit();
    } catch (error) {
      console.error('Error al crear el código de la ciudad.', error);
    }
  }

  async createNewRegister() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast('warning', `El usuario no tiene permisos para crear códigos ciudades.
        Comuníquese con el administrador del sistema.`);
      return;
    }
    await this.getAllLists('countries');
    this.resetContract();
  }

  async getAllLists(...listsToLoad: Array<'countries' | 'departments' | 'cities'>) {
    try {
      const lists = {
        countries: async () => {
          this.listCountries = await firstValueFrom(
            this.countryService.getAllCountriesForSelect()
          );
        },
        departments: async () => {
          if (this.countryId) {
            this.listDepartments = await firstValueFrom(
              this.departmentService.getAllDepartmentsByCountryIdForSelect(this.countryId)
            );
          }
        },
        cities: async () => {
          if (this.departmentId) {
            this.listCities = await firstValueFrom(
              this.cityService.getAllCitiesByDepartmentIdForSelect(this.departmentId)
            );
          }
        }
      };

      if (listsToLoad.length) {
        await Promise.all(listsToLoad.map(list => lists[list]()));
      } else {
        await Promise.all([
          lists.cities(),
          lists.countries(),
          lists.departments(),
        ]);
      }
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error obtener las listas.', error);
    }
  }

  async getCountry(countryId: number) {
    this.countryId = countryId;
    await this.getAllLists('departments');
    this.validateForm(new CompanyCityModel());
    this.formCompanyCity.controls['countryId'].patchValue(countryId);
  }

  async getDepartment(departmentId: number) {
    this.departmentId = departmentId;
    await this.getAllLists('cities');
    this.validateForm(new CompanyCityModel());
    this.formCompanyCity.controls['countryId'].patchValue(this.countryId);
    this.formCompanyCity.controls['departmentId'].patchValue(departmentId);
  }

  async getCompanyCityById() {
    try {
      const response = await firstValueFrom(this.companyCityService.getCompanyCityById(this.companyCityId));
      await this.getAllLists();
      this.countryId = response.city.department!.countryId;
      this.companyCityModel = response;
      this.validateForm(this.companyCityModel);
    } catch (error) {
      console.error('Error al obtener el código de la ciudad por id.', error);
    }
  }

  async getCompanyCityByCityId(cityId: number | null) {
    try {
      const response = await firstValueFrom(this.companyCityService.getCompanyCityByCityId(cityId));
      this.companyCityId = response.companyCityId;
      this.companyCityModel = response;
      this.validateForm(this.companyCityModel);
    } catch (error) {
      console.error('Error al obtener el código de la ciudad por id.', error);
    }
  }

  async getPermission() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast('error', `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`);
      }
    } else {
      localStorage.clear();
    }
  }

  async initializeFreightData() {
    this.updateFormPermision();
    this.companyCityId = this.companyCityModel.companyCityId;
    if (this.companyCityId) {
      this.departmentId = this.companyCityModel.city.departmentId;
      this.countryId = this.companyCityModel.city.department?.countryId ?? 0;
      await this.getAllLists();
      this.validateForm(this.companyCityModel);
    } else {
      await this.createNewRegister();
    }
  }

  onReloadEmit() {
    const output$ = this.config.data?.output$;
    output$?.next({ type: 'onReload', payload: true });
  }

  resetContract() {
    this.companyCityId = 0;
    this.companyCityModel = new CompanyCityModel();
    this.validateForm(this.companyCityModel);
  }

  async send() {
    if (!this.formCompanyCity.valid) {
      this.showValidationMessages();
      return;
    }
    const modelCompanyCity = this.formCompanyCity.getRawValue() as CompanyCityModel;
    if (this.companyCityId == 0) {
      await this.createCompanyCity(modelCompanyCity);
    } else {
      await this.updateCompanyCity(modelCompanyCity);
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      cityId: {
        required: 'Debe seleccionar una Ciudad.',
        min: 'Debe seleccionar una Ciudad válida.'
      },
      alterCode: {
        required: 'El Código Alterno es obligatorio.',
        maxlength: 'El Código Alterno no puede tener más de 10 caracteres.'
      }
    };

    Object.keys(controlMessages).forEach(controlName => {
      const control = this.formCompanyCity.get(controlName);
      if (control && control.invalid && control.errors) {
        const messages = controlMessages[controlName];
        Object.keys(control.errors).forEach(errorKey => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  updateFormPermision() {
    if (this.permissionUpdatable) {
      this.disabledForm = false;
      setTimeout(() => this.formCompanyCity.enable());
    } else {
      this.disabledForm = true;
      setTimeout(() => this.formCompanyCity.disable());
    }
  }

  async updateCompanyCity(modelCompanyCity: CompanyCityModel) {
    this.companyCityModel.alterCode = modelCompanyCity.alterCode;
    try {
      this.companyCityModel = await firstValueFrom(this.companyCityService.updateCompanyCity(this.companyCityModel));
      this.toastHandlerService.showToast('success', `El código de la ciudad fue modificado exitosamente.`);
      this.onReloadEmit();
    } catch (error) {
      console.error('Error al actualizar el código de la ciudad.', error);
    }
  }

  validateForm(companyCity: CompanyCityModel) {
    this.formCompanyCity = this.formBuilder.group({
      countryId: [companyCity.city.department?.countryId, [Validators.required]],
      departmentId: [companyCity.city.departmentId, [Validators.required, Validators.min(1)]],
      cityId: [companyCity.cityId, [Validators.required, Validators.min(1)]],
      alterCode: [companyCity.alterCode, [Validators.required, Validators.maxLength(10)]]
    });
  }
}
